import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <section
        className="ser-section text-center"
        style={{
          background: "linear-gradient(160deg, var(--ser-primary-dark) 0%, var(--ser-primary) 70%)",
          color: "white",
        }}
      >
        <div className="ser-container max-w-4xl">
          <p className="eyebrow mb-4" style={{ color: "var(--ser-clay)" }}>Get In Touch</p>
          <h1 className="font-serif-display text-5xl md:text-7xl mb-5">Contact us</h1>
          <p className="text-lg" style={{ color: "rgba(255,255,255,0.8)" }}>
            Questions, partnerships, or media enquiries — we'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="ser-section">
        <div className="ser-container max-w-6xl grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2 space-y-8">
            <div>
              <p className="eyebrow mb-3">Our details</p>
              <h2 className="font-serif-display text-3xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>
                Reach out directly.
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                Our team responds to all enquiries within 2–3 working days.
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "var(--ser-primary)" }}>
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--ser-primary-dark)" }}>Location</p>
                  <p className="text-sm" style={{ color: "var(--ser-text-muted)" }}>
                    South Africa
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "var(--ser-primary)" }}>
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--ser-primary-dark)" }}>Email</p>
                  <a href="mailto:africa@ser.org" className="text-sm hover:underline"
                    style={{ color: "var(--ser-text-muted)" }} data-testid="contact-email-link">
                    africa@ser.org
                  </a>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-3 ser-card p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="ser-label">Full name</label>
                <input required className="ser-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name" />
              </div>
              <div>
                <label className="ser-label">Email</label>
                <input required type="email" className="ser-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="ser-label">Phone <span className="text-xs font-normal" style={{ color: "var(--ser-text-muted)" }}>(optional)</span></label>
                <input className="ser-input" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone" />
              </div>
              <div>
                <label className="ser-label">Subject</label>
                <input required className="ser-input" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject" />
              </div>
            </div>
            <div>
              <label className="ser-label">Message</label>
              <textarea required rows={5} className="ser-input" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message" />
            </div>
            <button type="submit" disabled={submitting} className="btn-ser-primary" data-testid="submit-contact-btn">
              <Send className="w-4 h-4" />
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
